import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Spacing from '../Spacing';
import { pageTitle } from '../../helpers/PageTitle';
import { authAPI } from '../../services/api';

const emailSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required')
});

const resetSchema = Yup.object({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required')
});

export default function ForgotPasswordPage() {
  pageTitle('Forgot Password - Ayumedix');
  const [step, setStep] = useState(1); // 1: Email, 2: OTP & Reset
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef([]);
  const navigate = useNavigate();

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Only allow single digit
    
    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);
    
    // Auto focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const getOtpString = () => otpValues.join('');

  const handleEmailSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      console.log('Sending OTP to:', values.email);
      const response = await authAPI.forgotPassword({ email: values.email });
      console.log('Forgot password response:', response);
      
      if (response.data.statusCode === 200) {
        setEmail(values.email);
        setStep(2);
        toast.success('OTP sent to your email address');
      } else {
        toast.error(response.data.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleResetSubmit = async (values, { setSubmitting }) => {
    const otpString = getOtpString();
    if (otpString.length !== 6) {
      toast.error('Please enter complete 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const resetData = {
        email: email,
        otp: otpString,
        newPassword: values.password
      };
      
      console.log('Resetting password with:', resetData);
      const response = await authAPI.resetPassword(resetData);
      console.log('Reset password response:', response);
      
      if (response.data.statusCode === 200) {
        toast.success('Password reset successful! Please sign in with your new password.');
        setTimeout(() => navigate('/signin'), 2000);
      } else {
        toast.error(response.data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const resendOtp = async () => {
    try {
      console.log('Resending OTP to:', email);
      const response = await authAPI.forgotPassword({ email });
      
      if (response.data.statusCode === 200) {
        toast.success('OTP resent successfully');
      } else {
        toast.error(response.data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      toast.error('Failed to resend OTP');
    }
  };

  return (
    <>
      <Spacing lg="70" md="70" />
      <section className="cs_primary_bg">
        <Spacing lg="100" md="80" />
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-5 col-md-7">
              <div className="cs_signup_form_wrap" style={{
                background: 'rgba(0, 0, 0, 0.8)',
                borderRadius: '15px',
                padding: '40px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div className="cs_signup_form">
                  <div className="text-center mb-4">
                    <img src="/images/ayumedix.png" width={150} alt="Ayumedix Logo" />
                    <h2 className="cs_fs_40 cs_semibold mt-3 cs_white_color">
                      {step === 1 ? 'Forgot Password' : 'Reset Password'}
                    </h2>
                    <p className="cs_fs_16 cs_white_color">
                      {step === 1 ? 'Enter your email to receive OTP' : `Enter OTP sent to ${email}`}
                    </p>
                  </div>
                  
                  {step === 1 ? (
                    <Formik
                      initialValues={{ email: '' }}
                      validationSchema={emailSchema}
                      onSubmit={handleEmailSubmit}
                    >
                      {({ isSubmitting }) => (
                        <Form>
                          <div className="row">
                            <div className="col-12">
                              <label className="cs_fs_18 cs_semibold cs_white_color">
                                Email Address *
                              </label>
                              <Field
                                type="email"
                                name="email"
                                placeholder="Enter your email address"
                                className="cs_form_field"
                              />
                              <ErrorMessage name="email" component="div" style={{color: '#ff6b6b', fontSize: '14px', marginTop: '5px'}} />
                              <Spacing lg="30" md="25" />
                            </div>
                            
                            <div className="col-12 text-center">
                              <button 
                                type="submit" 
                                className="cs_btn cs_style_1 cs_fs_16"
                                disabled={isSubmitting || loading}
                              >
                                {loading ? 'Sending OTP...' : 'Send OTP'}
                                {!loading && (
                                  <span>
                                    <i><Icon icon="fa6-solid:arrow-right" /></i>
                                    <i><Icon icon="fa6-solid:arrow-right" /></i>
                                  </span>
                                )}
                              </button>
                              <Spacing lg="20" md="15" />
                              <p className="cs_fs_14 cs_white_color">
                                Remember your password? 
                                <Link to="/signin" className="cs_accent_color cs_semibold"> Sign In</Link>
                              </p>
                            </div>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  ) : (
                    <Formik
                      initialValues={{ password: '', confirmPassword: '' }}
                      validationSchema={resetSchema}
                      onSubmit={handleResetSubmit}
                    >
                      {({ isSubmitting }) => (
                        <Form>
                          <div className="row">
                            <div className="col-12">
                              <label className="cs_fs_18 cs_semibold cs_white_color">
                                Enter OTP *
                              </label>
                              <div className="cs_otp_container">
                                {otpValues.map((value, index) => (
                                  <input
                                    key={index}
                                    ref={el => otpRefs.current[index] = el}
                                    type="text"
                                    value={value}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                    className="cs_otp_input"
                                    maxLength="1"
                                  />
                                ))}
                              </div>
                              <div className="text-center mt-2">
                                <button 
                                  type="button" 
                                  onClick={resendOtp}
                                  className="cs_fs_14 cs_accent_color"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                >
                                  Resend OTP
                                </button>
                              </div>
                              <Spacing lg="25" md="20" />
                            </div>
                            
                            <div className="col-12">
                              <label className="cs_fs_18 cs_semibold cs_white_color">
                                New Password *
                              </label>
                              <Field
                                type="password"
                                name="password"
                                placeholder="Enter new password"
                                className="cs_form_field"
                              />
                              <ErrorMessage name="password" component="div" style={{color: '#ff6b6b', fontSize: '14px', marginTop: '5px'}} />
                              <Spacing lg="25" md="20" />
                            </div>
                            
                            <div className="col-12">
                              <label className="cs_fs_18 cs_semibold cs_white_color">
                                Confirm Password *
                              </label>
                              <Field
                                type="password"
                                name="confirmPassword"
                                placeholder="Confirm new password"
                                className="cs_form_field"
                              />
                              <ErrorMessage name="confirmPassword" component="div" style={{color: '#ff6b6b', fontSize: '14px', marginTop: '5px'}} />
                              <Spacing lg="30" md="25" />
                            </div>
                            
                            <div className="col-12 text-center">
                              <button 
                                type="submit" 
                                className="cs_btn cs_style_1 cs_fs_16"
                                disabled={isSubmitting || loading}
                              >
                                {loading ? 'Resetting...' : 'Reset Password'}
                                {!loading && (
                                  <span>
                                    <i><Icon icon="fa6-solid:arrow-right" /></i>
                                    <i><Icon icon="fa6-solid:arrow-right" /></i>
                                  </span>
                                )}
                              </button>
                              <Spacing lg="20" md="15" />
                              <button 
                                type="button" 
                                onClick={() => setStep(1)}
                                className="cs_fs_14 cs_white_color"
                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                              >
                                ← Back to Email
                              </button>
                            </div>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Spacing lg="100" md="80" />
      </section>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      
      <style jsx>{`
        .cs_otp_container {
          display: flex;
          gap: 10px;
          justify-content: center;
          margin: 15px 0;
        }
        .cs_otp_input {
          width: 50px;
          height: 50px;
          text-align: center;
          font-size: 18px;
          font-weight: 600;
          border: 2px solid #444;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.1);
          color: white;
          outline: none;
          transition: all 0.3s ease;
        }
        .cs_otp_input:focus {
          border-color: #0b5e4b;
          background: rgba(0, 218, 141, 0.1);
        }
        @media (max-width: 480px) {
          .cs_otp_input {
            width: 40px;
            height: 40px;
            font-size: 16px;
          }
          .cs_otp_container {
            gap: 8px;
          }
        }
      `}</style>
    </>
  );
}